import { EVENTS, publish, subscribe } from '@sendible/event-manager';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchWithHeaders, reportErrorToFaro } from '@sendible/common';
import { useTranslation } from 'react-i18next';
import { MediaIdType, MediaType, useBridgeContext } from '@sendible/shared-state-bridge';
import { useUploadFiles } from './fromFile';
import { useUploadUrls } from './fromUrl';
import { useUploadThumbnail } from './uploadThumbnail';
import { useUpdateThumbnailById } from './updateThumbnailById';
import { useErrorCodeMessageFinder } from './utils';
import endpoints from '../../data-layer/endpoints';
import { getUrlWithCredentialsFromUser } from '../../utils';
import { usePollMediasById } from '../medias';
import { singleFileUploadFailure, urlUploadFailure } from '../../pages/MediaLibrary/pendoEvents';

export type UploadsContextType = {
  uploadingMedia: UploadingMediaType[];
  addToUploadingMedia: (media: UploadingMediaType) => void;
  uploadFiles: (args: UploadFilesArgs) => Promise<void>;
  uploadUrls: (args: UploadUrlsArgs) => Promise<void>;
  uploadThumbnail: (args: UploadThumbnailArgs) => Promise<MediaIdType | null>;
  updateThumbnailById: (args: UpdateThumbnailByIdArgs) => Promise<MediaType | null>;
};

export const UploadsContext = createContext({} as UploadsContextType);

export const UploadsProvider = ({ children }: Component) => {
  const [uploadingMedia, setUploadingMedia] = useState<UploadingMediaType[]>([]);
  const cancelledMediaIds = useRef<Set<number>>(new Set());
  const uploadingMediaIds = useMemo(
    () => uploadingMedia.filter((media) => media.status === 'New' || media.status === 'Processing').map((media) => media.id),
    [uploadingMedia]
  );
  const [bridgeState] = useBridgeContext();
  const { t } = useTranslation('media_library');
  const errorCodeMessageFinder = useErrorCodeMessageFinder(uploadingMedia);
  const MAX_POLL_ERRORS = 3;
  const pollErrorCountRef = useRef<number>(0);
  const { data, isError, error: pollError, status } = usePollMediasById(uploadingMediaIds);

  const addToUploadingMedia = (media: UploadingMediaType) => {
    setUploadingMedia((prev) => [...prev, media]);
  };

  const extractErrorParamsFromApi = (error: unknown) => {
    type ErrorWithResponse = { response?: { data?: { error?: { code: string }; result?: { failureReason: string } }; status?: number } };

    const httpStatusErrorCode = (error as ErrorWithResponse)?.response?.status;
    const errorResponse = (error as ErrorWithResponse)?.response?.data;

    let errorCode = t('upload_error') as string;

    if (errorResponse?.error?.code) {
      errorCode = errorResponse.error.code;
    }
    if (errorResponse?.result?.failureReason) {
      errorCode = errorResponse.result.failureReason;
    }

    return { httpStatusErrorCode, errorCode };
  };

  const handleUploadError = (errorCode: string, httpStatusErrorCode: number | undefined, { file, fileId, mediaId, url }: UploadOnErrorArgsType) => {
    if (mediaId && cancelledMediaIds.current.has(mediaId)) return;

    const errorMessage = errorCodeMessageFinder(errorCode, { file, fileId, mediaId, url });

    publish(EVENTS.UPLOAD_FAILED, { fileId, mediaId, error: errorMessage });
    setUploadingMedia((prev) => prev.filter(({ id }) => id !== mediaId));

    if (file) {
      reportErrorToFaro(errorMessage, `UploadsModel uploadFiles ${httpStatusErrorCode}`);
      window.pendo.track(singleFileUploadFailure, {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        httpStatusErrorCode,
        errorMessage,
      });
    }

    if (url) {
      reportErrorToFaro(errorMessage, `UploadsModel uploadURL ${httpStatusErrorCode}`);
      window.pendo.track(urlUploadFailure, { url, httpStatusErrorCode, errorMessage });
    }
  };

  useEffect(() => {
    if (status === 'error' && pollError) {
      pollErrorCountRef.current += 1;
      if (pollErrorCountRef.current >= MAX_POLL_ERRORS) {
        uploadingMediaIds.forEach((mediaId) => {
          handleUploadError('PollMediaFailed', undefined, { mediaId });
        });
        pollErrorCountRef.current = 0;
      }
    } else if (status === 'success') {
      pollErrorCountRef.current = 0;
    }
  }, [isError, pollError]);

  useEffect(() => {
    data?.forEach((media) => {
      const mediaId = media.id;

      if (cancelledMediaIds.current.has(mediaId)) {
        setUploadingMedia((prev) => prev.filter(({ id }) => id !== media.id));
        cancelledMediaIds.current.delete(mediaId);

        return;
      }

      if (media.status === 'Failed') {
        const uploadingEntry = uploadingMedia.find(({ id }) => id === media.id);

        setUploadingMedia((prev) => prev.filter(({ id }) => id !== media.id));
        handleUploadError(media.components?.poster?.failureReason || media.failureReason || '', 0, { mediaId, fileId: uploadingEntry?.fileId });
      } else if (media.status === 'Ready') {
        publish(EVENTS.UPLOAD_READY, media);
        setUploadingMedia((prev) => prev.filter(({ id }) => id !== media.id));
      }

      setUploadingMedia((prev) => prev.map((m) => (m.id === media.id ? { ...m, ...media } : m)));
    });
  }, [data]);

  useEffect(() => {
    const unsubscribeCancelled = subscribe(EVENTS.UPLOAD_MEDIA_CANCELLED, (mediaId: number) => {
      const { method: deleteMethod, endpoint: deleteEndpoint } = endpoints.DeleteMedia;

      cancelledMediaIds.current.add(mediaId);

      let cancelUpload: (() => void) | undefined;

      publish(EVENTS.UPLOAD_FAILED, { mediaId, error: 'UploadCancelled' });

      setUploadingMedia((prev) => {
        cancelUpload = prev.find(({ id }) => id === mediaId)?.cancel;

        return prev.filter(({ id }) => id !== mediaId);
      });

      cancelUpload?.();

      if (bridgeState?.user && mediaId) {
        fetchWithHeaders({
          method: deleteMethod,
          url: getUrlWithCredentialsFromUser(deleteEndpoint, bridgeState?.user),
          params: { mediaId },
        });
      }
    });

    return () => {
      unsubscribeCancelled();
    };
  }, [bridgeState]);

  const uploadFiles = useUploadFiles({
    addToUploadingMedia,
    user: bridgeState?.user,
    onProgress: (fileId: string, progress: number) => publish(EVENTS.UPLOAD_PROGRESS, { fileId, uploadProgress: progress }),
    onError: (error, params) => {
      const { httpStatusErrorCode, errorCode } = extractErrorParamsFromApi(error);

      handleUploadError(errorCode, httpStatusErrorCode, params);
    },
  });

  const uploadUrls = useUploadUrls({
    addToUploadingMedia,
    user: bridgeState?.user,
    onError: (error, params) => {
      const { httpStatusErrorCode, errorCode } = extractErrorParamsFromApi(error);

      handleUploadError(errorCode, httpStatusErrorCode, params);
    },
  });

  const uploadThumbnail = useUploadThumbnail({
    user: bridgeState?.user,
    addToUploadingMedia,
    onError: (error, params) => {
      const { httpStatusErrorCode, errorCode } = extractErrorParamsFromApi(error);

      handleUploadError(errorCode, httpStatusErrorCode, params);
    },
  });

  const updateThumbnailById = useUpdateThumbnailById({
    user: bridgeState?.user,
    addToUploadingMedia,
    onError: (error, params) => {
      const { httpStatusErrorCode, errorCode } = extractErrorParamsFromApi(error);

      handleUploadError(errorCode, httpStatusErrorCode, params);
    },
  });

  return (
    <UploadsContext.Provider
      value={{
        uploadingMedia,
        addToUploadingMedia,
        uploadFiles,
        uploadUrls,
        uploadThumbnail,
        updateThumbnailById,
      }}
    >
      {children}
    </UploadsContext.Provider>
  );
};

export const useUploadsContext = (): UploadsContextType => useContext(UploadsContext);
